import { usePageSeoContext } from '../../lib/PageSeoContext';
import {
  ANNOUNCEMENT_BANNER_ENABLED_KEY,
  ANNOUNCEMENT_BANNER_TEXT_KEY,
  ANNOUNCEMENT_BANNER_STYLE_KEY,
  ANNOUNCEMENT_BANNER_COLOR_KEY,
  DEFAULT_ANNOUNCEMENT_BANNER_COLOR,
} from '../../lib/siteSettingsApi';

// Plain #rgb/#rrggbb hex only (matches the <input type="color"> that feeds
// this) — relative luminance decides whether white or dark ink text reads
// better on top of an admin-picked background.
function readableTextColor(hex: string): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-f]{6}$/i.test(full)) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.6 ? '#21201c' : '#ffffff';
}

export type AnnouncementBannerStyle = 'solid' | 'gold_shimmer';

/** The visual strip itself, split out from the settings-reading default
 * export so the admin Settings page can render a live preview of unsaved
 * changes through the exact same markup instead of a hand-approximated copy. */
export function AnnouncementBannerContent({ text, style, color }: { text: string; style: AnnouncementBannerStyle; color: string }) {
  const isGold = style === 'gold_shimmer';
  return (
    <div
      className={isGold ? 'hs-gold text-gold-ink' : 'text-paper'}
      style={isGold ? undefined : { background: color, color: readableTextColor(color) }}
    >
      <p className="max-w-page mx-auto px-5 md:px-8 py-2 text-center text-[12.5px] md:text-[13.5px] font-semibold leading-snug m-0">
        {text}
      </p>
    </div>
  );
}

/** Sticky admin-configurable strip above the nav header — a seasonal offer,
 * a certification callout, etc. Renders nothing unless enabled with text. */
export default function AnnouncementBanner() {
  const { siteSettings } = usePageSeoContext();
  const enabled = siteSettings[ANNOUNCEMENT_BANNER_ENABLED_KEY] === '1';
  const text = siteSettings[ANNOUNCEMENT_BANNER_TEXT_KEY]?.trim() ?? '';
  const style = siteSettings[ANNOUNCEMENT_BANNER_STYLE_KEY] === 'gold_shimmer' ? 'gold_shimmer' : 'solid';
  const color = siteSettings[ANNOUNCEMENT_BANNER_COLOR_KEY] || DEFAULT_ANNOUNCEMENT_BANNER_COLOR;

  if (!enabled || !text) return null;

  return <AnnouncementBannerContent text={text} style={style} color={color} />;
}
